export type SearchableListMatch<T> = (item: T, lowerQuery: string) => boolean;

export class SearchableList<T extends { id: number | string }> {
    items: T[] = [];
    filtered: T[] = [];
    display: T[] = [];
    query = '';

    private baseFilter: Set<number | string> | null = null;

    constructor(
        private readonly match: SearchableListMatch<T>,
        private readonly selectedIds?: () => (number | string)[],
    ) {}

    setItems(items: T[]): void {
        this.items = items;
        this.applyFilters();
    }

    search(query: string): void {
        this.query = query;
        this.applyFilters();
    }

    setBaseFilter(ids: Set<number | string> | null): void {
        this.baseFilter = ids;
        this.applyFilters();
    }

    syncSelection(): void {
        this.refreshDisplay();
    }

    prune<K extends number | string>(ids: K[]): K[] | null {
        const known = new Set<number | string>(this.items.map(item => item.id));
        const valid = ids.filter(id => known.has(id));
        return valid.length === ids.length ? null : valid;
    }

    private applyFilters(): void {
        const afterSearch = !this.query
            ? this.items
            : this.items.filter(item => this.match(item, this.query.toLowerCase()));
        const base = this.baseFilter;
        this.filtered = base
            ? afterSearch.filter(item => base.has(item.id))
            : afterSearch;
        this.refreshDisplay();
    }

    private refreshDisplay(): void {
        if (!this.selectedIds) {
            this.display = this.filtered;
            return;
        }
        const selected = new Set(this.selectedIds());
        const extras = this.items.filter(item => selected.has(item.id) && !this.filtered.includes(item));
        this.display = extras.length ? [...this.filtered, ...extras] : this.filtered;
    }
}
